import useIsFeatureEnabled from '@app/hooks/useIsFeatureEnabled'
import { type ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { SubscriptionContext, type SubscriptionContextValue } from './SubscriptionContext'

const SubscriptionContextProvider = ({ children }: { children: ReactNode }) => {
  const subscriptionVersion = useIsFeatureEnabled('growthPricingV2') ? 'v2' : 'v1'
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubscriptionInProgress, setIsSubscriptionInProgressState] = useState(false)
  // Mirrors isSubscriptionInProgress synchronously, so a second click in the same render is also rejected
  const isSubscriptionInProgressRef = useRef(false)
  const [planFormValid, setPlanFormValid] = useState<boolean>(false)
  const [billingDetailsFormValid, setBillingDetailsFormValid] = useState<boolean>(false)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [inAuthStep, setInAuthStep] = useState(false)
  const [msaConsent, setMsaConsent] = useState(false)
  const [showMsaError, setShowMsaError] = useState(false)

  const handleMSAChange = useCallback(
    (checked: boolean) => {
      if (showMsaError && checked) {
        setShowMsaError(false)
      }
      setMsaConsent(checked)
    },
    [showMsaError],
  )

  const setIsSubscriptionInProgress = useCallback((inProgress: boolean) => {
    isSubscriptionInProgressRef.current = inProgress
    setIsSubscriptionInProgressState(inProgress)
  }, [])

  const tryStartSubscription = useCallback(() => {
    if (isSubscriptionInProgressRef.current) {
      return false
    }
    setIsSubscriptionInProgress(true)
    return true
  }, [setIsSubscriptionInProgress])

  const handleSubscribeClick = useCallback(() => {
    if (!msaConsent) {
      setShowMsaError(true)
      return
    }
    setShowMsaError(false)
    setIsSubmitting(true)
  }, [msaConsent])

  useLayoutEffect(() => {
    if (isSubmitting && (!billingDetailsFormValid || !planFormValid)) {
      setIsSubmitting(false)
    }

    if (isSubmitting && billingDetailsFormValid && planFormValid) {
      setIsConfirmationOpen(true)
      setIsSubmitting(false)
    }
  }, [isSubmitting, billingDetailsFormValid, planFormValid])

  const memoizedValue = useMemo(
    () =>
      ({
        isSubmitting,
        setIsSubmitting,
        isSubscriptionInProgress,
        setIsSubscriptionInProgress,
        tryStartSubscription,
        planFormValid,
        setPlanFormValid,
        billingDetailsFormValid,
        setBillingDetailsFormValid,
        isConfirmationOpen,
        setIsConfirmationOpen,
        inAuthStep,
        setInAuthStep,
        msaConsent,
        setMsaConsent,
        showMsaError,
        setShowMsaError,
        handleMSAChange,
        handleSubscribeClick,
        subscriptionVersion,
      }) satisfies SubscriptionContextValue,
    [
      isSubmitting,
      setIsSubmitting,
      isSubscriptionInProgress,
      setIsSubscriptionInProgress,
      tryStartSubscription,
      planFormValid,
      setPlanFormValid,
      billingDetailsFormValid,
      setBillingDetailsFormValid,
      isConfirmationOpen,
      setIsConfirmationOpen,
      inAuthStep,
      msaConsent,
      showMsaError,
      handleMSAChange,
      handleSubscribeClick,
      subscriptionVersion,
    ],
  )

  return (
    <SubscriptionContext.Provider value={memoizedValue}>{children}</SubscriptionContext.Provider>
  )
}

export default SubscriptionContextProvider
